import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, MessageSquare, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { projectsAPI, usersAPI } from '../../services/api';
import { getStatusLabel, getStatusBadgeClass } from '../../utils/statusHelpers';
import { formatRoleLabel } from '../../utils/roles';
import useAuthStore from '../../context/authStore';
import { consultingServices } from '../../../../shared/consultingServices';

import BackButton from '../../components/common/BackButton';
const consultingServiceOptions = consultingServices.map((service) => ({
  value: service.id,
  label: service.label,
  categories: (service.subcategories || []).map((s) => s.label),
}));
const serviceTypes = consultingServiceOptions.map(({ value, label }) => ({ value, label }));

export default function ProjectDetailPage() {
  const { id } = useParams();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clientContacts, setClientContacts] = useState([]);
  const [clientLeadId, setClientLeadId] = useState('');
  const [selectedClientUserId, setSelectedClientUserId] = useState('');
  const [savingContacts, setSavingContacts] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteRole, setInviteRole] = useState('facility_worker');
  const [inviting, setInviting] = useState(false);
  const [showInviteValidation, setShowInviteValidation] = useState(false);
  const [showClientCollab, setShowClientCollab] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectsAPI.getById(id),
    // Defensive: some responses return { success, project } others return { data: { project } }
    select: (res) => res.data?.project || res.project || res.data,
    onError: (err) => {
      console.error('Client project fetch error', err);
    },
    retry: false,
  });

  useEffect(() => {
    console.debug('Client Project load state', { isLoading, error, data });
  }, [isLoading, error, data]);

  const orgId = data?.clientId?._id || data?.clientId;

  const normalizeStatus = (status) => {
    const s = (status || '').toString().trim().toLowerCase();
    const allowed = new Set([
      'active', 'in_progress', 'in_review', 'completed', 'on_hold', 'archived', 'canceled',
    ]);
    return allowed.has(s) ? s : 'active';
  };

  const isCompleted = normalizeStatus(data?.status) === 'completed';

  // Change request modal state
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [changes, setChanges] = useState([]);
  const [submittingChange, setSubmittingChange] = useState(false);

  const FIELD_OPTIONS = [
    { value: 'name', label: 'Project Name', type: 'text' },
    { value: 'description', label: 'Description', type: 'textarea' },
    { value: 'serviceType', label: 'Service Type', type: 'select',
      options: serviceTypes.map(t => ({ value: t.value, label: t.label })) },
    { value: 'dates.kickoff', label: 'Kickoff Date', type: 'date' },
    { value: 'dates.targetCompletion', label: 'Target Completion', type: 'date' },
    { value: 'serviceOffers', label: 'Proposed Services (describe)', type: 'textarea' },
    { value: 'other', label: 'Other', type: 'textarea' },
  ];

  const addChangeRow = () => setChanges((c) => [...c, { field: 'name', after: '', note: '' }]);
  const updateChangeRow = (idx, patch) => setChanges((c) => c.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeChangeRow = (idx) => setChanges((c) => c.filter((_, i) => i !== idx));

  const { data: usersData } = useQuery({
    queryKey: ['clientProjectUsers', id],
    queryFn: () => usersAPI.getAll(),
    select: (res) => res.data?.users || res.data || [],
    enabled: !!data,
  });

  const { data: pendingInvites = [], refetch: refetchInvites } = useQuery({
    queryKey: ['clientProjectInvites', orgId, id],
    queryFn: async () => {
      const res = await api.get('/invitations', {
        params: { organizationId: orgId, status: 'pending', projectId: id },
      });
      return res.data?.invitations || res.invitations || [];
    },
    enabled: !!orgId,
  });

  useEffect(() => {
    if (!data) return;
    setClientContacts(data.clientContacts || []);
    const leadContact = data.clientContacts?.[0]?._id || data.clientContacts?.[0] || '';
    setClientLeadId(leadContact);
  }, [data]);

  useEffect(() => {
    if (!data) return;
    if (searchParams.get('requestChange') === '1') {
      setShowChangeModal(true);
    }
  }, [data, searchParams]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    const is403 = error?.response?.status === 403 || error?.status === 403;
    if (is403) {
      navigate('/projects', { replace: true });
      return null;
    }
    return (
      <div className="text-center py-12 flex flex-col gap-4">
        <div className="text-red-500">Failed to load project{error?.message ? `: ${error.message}` : ''}</div>
        <div className="text-sm text-gray-600">Try reloading or check the console/network tab for details.</div>
        <div className="flex gap-2 justify-center">
          <button onClick={() => refetch()} className="btn-primary">Retry</button>
          <BackButton fallbackTo={"/projects"} plainClassName="inline-flex items-center gap-2 btn-secondary" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  // (normalizeStatus defined above)

  const getUserId = (user) => user?._id || user;
  const clientUsers = (usersData || []).filter((user) => {
    const userOrgId = user.organizationId?._id || user.organizationId;
    return orgId && userOrgId?.toString() === orgId.toString();
  });

  const handleAddClient = () => {
    if (!selectedClientUserId) return;
    if (clientContacts.some((contact) => getUserId(contact) === selectedClientUserId)) {
      return;
    }
    const selectedUser = clientUsers.find((user) => user._id === selectedClientUserId);
    if (!selectedUser) return;
    const nextContacts = [...clientContacts, selectedUser];
    setClientContacts(nextContacts);
    if (!clientLeadId) {
      setClientLeadId(selectedClientUserId);
    }
    setSelectedClientUserId('');
  };

  const handleRemoveClient = (userId) => {
    const nextContacts = clientContacts.filter((contact) => getUserId(contact) !== userId);
    setClientContacts(nextContacts);
    if (clientLeadId === userId) {
      setClientLeadId(nextContacts[0]?._id || '');
    }
  };

  const handleSaveContacts = async () => {
    setShowValidation(true);
    const orderedContacts = clientLeadId
      ? [
        clientLeadId,
        ...clientContacts
          .map((contact) => getUserId(contact))
          .filter((contactId) => contactId !== clientLeadId),
      ]
      : clientContacts.map((contact) => getUserId(contact));

    if (!clientLeadId || orderedContacts.length === 0) {
      toast.error('Select a client lead before saving.');
      return;
    }

    if (!orderedContacts.includes(clientLeadId)) {
      toast.error('Client lead must be included in the collaborators list.');
      return;
    }

    try {
      setSavingContacts(true);
      await projectsAPI.updateContacts(id, orderedContacts);
      toast.success('Collaborators updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update collaborators');
    } finally {
      setSavingContacts(false);
    }
  };

  const handleInviteCollaborator = async () => {
    setShowInviteValidation(true);
    if (!inviteEmail || !inviteFirstName || !inviteLastName) {
      toast.error('Enter a name and email to invite a collaborator.');
      return;
    }

    try {
      setInviting(true);
      await api.post('/invitations', {
        email: inviteEmail,
        role: inviteRole,
        organizationId: orgId,
        projectId: id,
      });

      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      setInviteRole('facility_worker');
      setShowInviteValidation(false);
      await refetchInvites();
      toast.success('Invite sent');
    } catch (error) {
      toast.error(error.message || 'Failed to invite collaborator');
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await api.post(`/invitations/${inviteId}/resend`);
      toast.success('Invitation resent');
      await refetchInvites();
    } catch (error) {
      toast.error(error.message || 'Failed to resend invite');
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await api.delete(`/invitations/${inviteId}`);
      toast.success('Invitation revoked');
      await refetchInvites();
    } catch (error) {
      toast.error(error.message || 'Failed to revoke invite');
    }
  };

  const tabs = [
    { name: 'Documents', href: `/projects/${id}/documents`, icon: FileText },
    { name: 'Messages', href: `/projects/${id}/messages`, icon: MessageSquare },
  ];

  const getOrgInitials = (orgName) => {
    if (!orgName) return 'Client';
    const parts = orgName.trim().split(/\s+/).slice(0, 2);
    return parts.map((part) => part[0]?.toUpperCase()).join('');
  };

  const formatNameWithLastInitial = (person) => {
    const firstName = person?.firstName || person?.name || '';
    const lastInitial = person?.lastName ? `${person.lastName[0]}.` : '';
    if (!firstName && !lastInitial) return 'Not set';
    return `${firstName}${lastInitial ? ` ${lastInitial}` : ''}`.trim();
  };

  const orgLabel = getOrgInitials(data?.clientId?.name);

  const isActiveUser = (person) => person?.isActive !== false;
  const activeClientContacts = clientContacts.filter((contact) => {
    if (contact && contact._id) return isActiveUser(contact);
    const user = clientUsers.find((item) => item._id === contact);
    return isActiveUser(user);
  });
  const activeInternalTeamCount = (data.team || []).filter((member) =>
    isActiveUser(member.userId)
  ).length;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <BackButton fallbackTo={"/projects"} plainClassName="inline-flex items-center gap-2 text-secondary-600 hover:text-secondary-900" />

      {/* Completed banner */}
      {isCompleted && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="font-semibold text-gray-900">Project Completed</p>
            <p className="text-sm text-gray-600 mt-0.5">This project is read-only. You can still view documents and messages.</p>
          </div>
          <Link
            to={`/projects/${id}/messages`}
            className="btn-secondary shrink-0"
          >
            Message Your Consultant
          </Link>
        </div>
      )}

      {/* Header */}
      <div className="card p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-secondary-900">
              {data.name}
            </h1>
            <p className="mt-1 text-secondary-600">
              {data.clientId?.name} • {data.serviceCategory}
            </p>
          </div>
          <span className={`badge ${getStatusBadgeClass(data.status)}`}>
            {getStatusLabel(data.status)}
          </span>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-secondary-600">
          <span className="px-2 py-1 bg-secondary-100 rounded-full">
            SC Lead: {formatNameWithLastInitial(clientContacts?.[0])}
          </span>
          <span className="px-2 py-1 bg-secondary-100 rounded-full">
            {orgLabel} Team: {clientContacts?.length || 0}
          </span>
        </div>

        {data.description && (
          <p className="mt-4 text-secondary-600">{data.description}</p>
        )}

        {showChangeModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4"
            onClick={() => setShowChangeModal(false)}
          >
            <div
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-lg"
              style={{
                width: '100%',
                maxWidth: '600px',
                margin: '0 auto',
                padding: 0,
                boxSizing: 'border-box',
              }}
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 mb-4 sm:mb-6">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Request a Change</h3>
                  <p className="text-sm text-gray-500 mt-1 sm:mt-0">Describe what you'd like updated. Our team will review and apply approved changes.</p>
                </div>
                <button onClick={() => setShowChangeModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl sm:text-base">×</button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Summary</label>
                  <input
                    value={changeSummary}
                    onChange={(e) => setChangeSummary(e.target.value)}
                    className="input w-full mt-1 text-base sm:text-sm"
                    style={{ fontSize: '1rem', padding: '0.5rem' }}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Changes</label>
                  <div className="space-y-2 mt-2">
                    {changes.map((row, idx) => {
                      const opt = FIELD_OPTIONS.find((o) => o.value === row.field) || FIELD_OPTIONS[0];
                      return (
                        <div key={idx} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-start">
                          <select value={row.field} onChange={(e) => updateChangeRow(idx, { field: e.target.value, after: '' })} className="input w-full sm:w-40">
                            {FIELD_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>

                          {opt.type === 'text' && (
                            <input value={row.after} onChange={(e) => updateChangeRow(idx, { after: e.target.value })} placeholder="New value" className="input flex-1" style={{ fontSize: '1rem', padding: '0.5rem' }} />
                          )}

                          {opt.type === 'textarea' && (
                            <textarea value={row.after} onChange={(e) => updateChangeRow(idx, { after: e.target.value })} placeholder="New value" className="input flex-1 h-20" style={{ fontSize: '1rem', padding: '0.5rem' }} />
                          )}

                          {opt.type === 'select' && (
                            <select value={row.after} onChange={(e) => updateChangeRow(idx, { after: e.target.value })} className="input w-full sm:w-64">
                              <option value="">Select</option>
                              {opt.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          )}

                          {opt.type === 'date' && (
                            <input type="date" value={row.after} onChange={(e) => updateChangeRow(idx, { after: e.target.value })} className="input" style={{ fontSize: '1rem', padding: '0.5rem' }} />
                          )}

                          <input placeholder="Note (optional)" value={row.note} onChange={(e) => updateChangeRow(idx, { note: e.target.value })} className="input w-full sm:w-48" style={{ fontSize: '1rem', padding: '0.5rem' }} />

                          <button className="btn-secondary w-full sm:w-auto mt-2 sm:mt-0" onClick={() => removeChangeRow(idx)}>Remove</button>
                        </div>
                      );
                    })}

                    <div>
                      <button className="btn-outline w-full sm:w-auto" onClick={addChangeRow}>Add change</button>
                    </div>
                    <p className="text-xs text-secondary-500 mt-1">Fields will be reviewed by our team; only approved fields will be applied automatically.</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 justify-end mt-4">
                  <button className="btn-secondary w-full sm:w-auto" onClick={() => setShowChangeModal(false)}>Cancel</button>
                  <button className="btn-primary w-full sm:w-auto" disabled={submittingChange} onClick={async () => {
                    try {
                      if (!changeSummary || !Array.isArray(changes) || changes.length === 0) {
                        throw new Error('Provide a summary and at least one change');
                      }
                      setSubmittingChange(true);
                      const payload = { summary: changeSummary, changes };
                      await projectsAPI.createChangeRequest(id, payload);
                      toast.success('Change request submitted — our team will review it shortly');
                      setShowChangeModal(false);
                    } catch (err) {
                      console.error(err);
                      toast.error(err.message || 'Failed to submit change request');
                    } finally {
                      setSubmittingChange(false);
                    }
                  }}>Submit Request</button>
                </div>
              </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick stats */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-4">
          <div className="p-4 bg-secondary-50 rounded-lg">
            <p className="text-sm text-secondary-500">Service Type</p>
            <p className="font-medium text-secondary-900 capitalize">{data.serviceType}</p>
          </div>
          <div className="p-4 bg-secondary-50 rounded-lg">
            <p className="text-sm text-secondary-500">My Team Size</p>
            <p className="font-medium text-secondary-900">{activeClientContacts.length} members</p>
          </div>
          <div className="p-4 bg-secondary-50 rounded-lg">
            <p className="text-sm text-secondary-500">Project Team Size</p>
            <p className="font-medium text-secondary-900">
              {activeInternalTeamCount + activeClientContacts.length} members
            </p>
          </div>
          <div className="p-4 bg-secondary-50 rounded-lg">
            <p className="text-sm text-secondary-500">Started</p>
            <p className="font-medium text-secondary-900">
              {data.dates?.kickoff ? new Date(data.dates.kickoff).toLocaleDateString() : 'TBD'}
            </p>
          </div>
          <div className="p-4 bg-secondary-50 rounded-lg">
            <p className="text-sm text-secondary-500">Target Completion</p>
            <p className="font-medium text-secondary-900">
              {data.dates?.targetCompletion ? new Date(data.dates.targetCompletion).toLocaleDateString() : 'TBD'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation tabs */}
      <div className="flex gap-4 border-b border-secondary-200">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <Link
              key={tab.name}
              to={tab.href}
              className="flex items-center gap-2 px-4 py-3 text-secondary-600 hover:text-secondary-900 border-b-2 border-transparent hover:border-primary-600 -mb-px"
            >
              <Icon size={18} />
              {tab.name}
            </Link>
          );
        })}
      </div>

      {/* Project team */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-secondary-900">Project Team</h2>
        </div>
        <div className="p-6">
          {data.team?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.team.map((member, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg">
                  <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium">
                      {member.userId?.firstName?.[0]}{member.userId?.lastName?.[0]}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-secondary-900">
                      {member.userId?.firstName} {member.userId?.lastName}
                    </p>
                    <p className="text-sm text-secondary-500 capitalize">{member.role}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-secondary-500">No team members assigned</p>
          )}
          {activeClientContacts.length > 0 && (
            <>
              <div className="mt-6 flex items-center gap-2 text-sm font-medium text-secondary-700">
                {orgLabel} Collaborators
              </div>
              <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeClientContacts.map((contact) => {
                  const contactId = getUserId(contact);
                  const contactUser = contact._id
                    ? contact
                    : clientUsers.find((item) => item._id === contactId) || {};
                  return (
                    <div
                      key={contactId}
                      className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg"
                    >
                      <div className="w-10 h-10 bg-secondary-300 rounded-full flex items-center justify-center">
                        <span className="text-white font-medium">
                          {contactUser.firstName?.[0]}
                          {contactUser.lastName?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-secondary-900">
                          {contactUser.firstName} {contactUser.lastName}
                        </p>
                        <p className="text-sm text-secondary-500">{contactUser.email}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Client collaborators — hidden for completed projects */}
      {!isCompleted && <div className="card">
        <div className="card-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">{orgLabel} Collaborators</h2>
            <p className="text-sm text-secondary-500">
              Add or update your client-side reviewers and select a lead.
            </p>
          </div>
          <button
            onClick={() => setShowClientCollab((prev) => !prev)}
            className="btn-secondary"
          >
            {showClientCollab ? `Hide ${orgLabel} Collaborators` : `Add ${orgLabel} Collaborators`}
          </button>
        </div>
        {showClientCollab && (
          <div className="p-6 space-y-4">
          <div className="space-y-2">
            {showValidation && (!clientLeadId || clientContacts.length === 0) && (
              <p className="text-sm text-red-600">
                Add at least one collaborator and select a lead before saving.
              </p>
            )}
            {clientContacts.length === 0 ? (
              <p className="text-secondary-500">No client collaborators selected</p>
            ) : (
              clientContacts.map((contact) => {
                const contactId = getUserId(contact);
                const contactUser = contact._id ? contact : clientUsers.find((user) => user._id === contactId) || {};
                const isLead = clientLeadId === contactId;
                return (
                  <div
                    key={contactId}
                    className="flex flex-wrap items-center justify-between gap-3 p-3 bg-secondary-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-secondary-900">
                        {contactUser.firstName} {contactUser.lastName}
                      </p>
                      <p className="text-sm text-secondary-500">{contactUser.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isLead && (
                        <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full">
                          Lead
                        </span>
                      )}
                      <button
                        onClick={() => handleRemoveClient(contactId)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select
              value={selectedClientUserId}
              onChange={(e) => setSelectedClientUserId(e.target.value)}
              className="input"
            >
              <option value="">Select teammate...</option>
              {clientUsers.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
            <button
              onClick={handleAddClient}
              className="btn-primary"
            >
              Add Collaborator
            </button>
          </div>

          {clientContacts.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                {orgLabel} Lead
              </label>
              <select
                value={clientLeadId}
                onChange={(e) => setClientLeadId(e.target.value)}
                className="input"
              >
                {clientContacts.map((contact) => {
                  const contactId = getUserId(contact);
                  const contactUser = contact._id ? contact : clientUsers.find((user) => user._id === contactId) || {};
                  return (
                    <option key={contactId} value={contactId}>
                      {contactUser.firstName} {contactUser.lastName}
                    </option>
                  );
                })}
              </select>
              {showValidation && !clientLeadId && (
                <p className="text-sm text-red-600 mt-2">
                  Select a client lead to continue.
                </p>
              )}
            </div>
          )}

          {user?.role === 'facility_admin' && (
            <div className="rounded-lg border border-secondary-200 p-4 space-y-3">
              <div>
                <p className="text-sm font-medium text-secondary-900">Invite collaborator</p>
                <p className="text-xs text-secondary-500">
                  Invite a teammate who does not have portal access yet.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  type="text"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                  placeholder="First name"
                  className="input"
                />
                <input
                  type="text"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                  placeholder="Last name"
                  className="input"
                />
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Email address"
                  className="input sm:col-span-2"
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="input sm:col-span-2"
                >
                  <option value="facility_worker">Facility Team Member (Training)</option>
                  <option value="facility_manager">Facility Manager</option>
                  <option value="facility_admin">Facility Admin</option>
                </select>
              </div>
              {showInviteValidation && (!inviteFirstName.trim() || !inviteLastName.trim()) && (
                <p className="text-sm text-red-600">First and last name are required.</p>
              )}
              {showInviteValidation && !/^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(inviteEmail) && (
                <p className="text-sm text-red-600">Enter a valid email address.</p>
              )}
              <div className="flex justify-end">
                <button
                  onClick={handleInviteCollaborator}
                  disabled={inviting}
                  className="btn-primary"
                >
                  {inviting ? 'Inviting...' : 'Send Invite'}
                </button>
              </div>
            </div>
          )}

          {pendingInvites.length > 0 && (
            <div className="rounded-lg border border-secondary-200 p-4 space-y-2">
              <p className="text-sm font-medium text-secondary-900">Pending Invites</p>
              {pendingInvites.map((invite) => (
                <div
                  key={invite._id || invite.id}
                  className="flex flex-wrap items-center justify-between gap-3 p-2 bg-yellow-50 rounded"
                >
                  <div>
                    <p className="text-sm font-medium text-secondary-900">{invite.email}</p>
                    <p className="text-xs text-secondary-500">{formatRoleLabel(invite.role)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                      Pending invite
                    </span>
                    <button
                      onClick={() => handleResendInvite(invite._id || invite.id)}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Resend
                    </button>
                    <button
                      onClick={() => handleRevokeInvite(invite._id || invite.id)}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleSaveContacts}
              disabled={savingContacts}
              className="btn-primary"
            >
              {savingContacts ? 'Saving...' : 'Save Collaborators'}
            </button>
          </div>
          </div>
        )}
      </div>}
    </div>
  );
}
